import React, { useCallback } from 'react';

import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import { SideNavBody, SideNav, SideNavHeader } from '@atlaskit/navigation-system/layout/side-nav';
import { MenuSection } from '@atlaskit/side-nav-items/menu-section';
import { FormattedMessage } from 'react-intl';

import { useSendSideNavItemClick } from '@townsquare/analytics-nav4';
import localStorageFacade from '@townsquare/facade/local-storage';
import { MenuItem } from '@townsquare/griffin-navigation/side-nav-menu-item';
import { OnClickType } from '@townsquare/griffin-navigation/types';
import { useRouter } from '@townsquare/router/useRouter';
import { openInNewTab } from '@townsquare/ui-interactions';

import { SettingsHeader } from './SettingsHeader';
import { useSideNavSettingsItems } from './use-side-nav-settings-items';

const NAV_COLLAPSED_LS_KEY = 'is-griffing-nav-collapsed';
const NAV_WIDTH_LS_KEY = 'nav4-left-sidenav-width';

const DEFAULT_SIDE_NAV_WIDTH = 320;
const getSideNavWidth = () => {
	const width = localStorageFacade.getItem(NAV_WIDTH_LS_KEY);
	return (width && Number.parseInt(width)) || DEFAULT_SIDE_NAV_WIDTH;
};

const isSideNavCollapsed = () => localStorageFacade.getItem(NAV_COLLAPSED_LS_KEY) === 'true';

export const SettingsSidebarNavigation = (): React.JSX.Element => {
	const defaultCollapsed = isSideNavCollapsed();
	const defaultWidth = getSideNavWidth();
	const [routerStore, routerActions] = useRouter();
	const settingsItems = useSideNavSettingsItems();

	const sendSideNavItemClick = useSendSideNavItemClick();

	const onToggle = (isCollapsed: boolean) => {
		localStorageFacade.setItem(NAV_COLLAPSED_LS_KEY, isCollapsed.toString());
	};

	const onResize = (options: { finalWidth: number }) => {
		localStorageFacade.setItem(NAV_WIDTH_LS_KEY, String(options.finalWidth));
	};

	const onClick: OnClickType = useCallback(
		(item, event) => {
			event.preventDefault();
			if ('path' in item && item.path) {
				if (event.metaKey || item.openInNewTab) {
					openInNewTab(item.path);
				} else {
					void routerActions.push(item.path);
				}
			}
			sendSideNavItemClick('navigationItem', item.analyticsSubject, {
				screen: routerStore.route.name,
			});
		},
		[sendSideNavItemClick, routerActions, routerStore.route.name],
	);

	return (
		<SideNav
			defaultWidth={defaultWidth}
			onCollapse={() => onToggle(true)}
			onExpand={() => onToggle(false)}
			defaultCollapsed={defaultCollapsed}
			testId="teams-settings-sidebar"
		>
			<SideNavHeader>
				<SettingsHeader />
			</SideNavHeader>
			<SideNavBody>
				<MenuSection>
					{settingsItems.map((item) => (
						<MenuItem key={item.id} item={item} onClick={onClick} />
					))}
				</MenuSection>
			</SideNavBody>
			<PanelSplitter
				label={
					<FormattedMessage
						id="townsquare.navigation.sidebar.resize"
						defaultMessage="Resize side navigation"
						description="Accessible label of the button in the sidebar's resizing component"
					/>
				}
				onResizeEnd={onResize}
			/>
		</SideNav>
	);
};
